import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header2";
import CheckoutProgress from "../../components/ui/CheckoutProgress";
import CartItem from "./components/CartItem";
import OrderSummary from "./components/OrderSummary";
import EmptyCart from "./components/EmptyCart";
import RecommendedProducts from "./components/RecommendedProducts";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteCartService,
  getMyCartService,
} from "redux/services/general_service";
import { toast } from "react-toastify";
import Footer from "../../components/common/Footer";

const ShoppingCart = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { isLoadingCart } = useSelector((state) => state.general);

  const isAuthenticated = localStorage.getItem("isAuthenticated");

  const [cartItems, setCartItems] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  const loadCartData = async () => {
    if (isAuthenticated) {
      // جلب البيانات من API عند تسجيل الدخول
      try {
        const response = await dispatch(getMyCartService());
        if (response?.payload?.status === 200) {
          setCartItems(response?.payload?.data?.data);
          setIsLoading(false);
        } else {
          // التعامل مع الأخطاء من API
          console.error("Failed to load cart from API:", response);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error loading cart data:", error);
        setIsLoading(false);
      }
    } else {
      try {
        const cartItems = JSON.parse(localStorage.getItem("cartItems") || "[]");
        console.log("Cart items from localStorage:", cartItems);
        setCartItems(cartItems);
        setIsLoading(false);
      } catch (error) {
        console.error("Error parsing cart data from localStorage:", error);
        setCartItems([]);
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    // Simulate loading cart data

    loadCartData();
  }, []);

  // Calculate totals
  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  const shipping = subtotal > 50 ? 0 : 9.99;
  const tax = subtotal * 0.08; // 8% tax
  const total = subtotal + shipping + tax;

  const handleUpdateQuantity = (itemId, newQuantity) => {
    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const handleRemoveItem = (itemId) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
  };

  const handleMoveToWishlist = (itemId) => {
    // Mock move to wishlist functionality
    const item = cartItems.find((item) => item.id === itemId);
    if (item) {
      handleRemoveItem(itemId);
      // Show success message (in real app, would save to wishlist)
      console.log(`Moved ${item.name} to wishlist`);
    }
  };

  const handleApplyPromoCode = (promoCode) => {
    // Mock promo code application
    console.log(`Applied promo code: ${promoCode}`);
  };

  const handleProceedToCheckout = () => {
    setIsCheckoutLoading(true);

    // Simulate checkout preparation
    setTimeout(() => {
      setIsCheckoutLoading(false);
      navigate("/checkout");
    }, 1500);
  };

  const handleAddToCart = (product) => {
    // Mock add to cart functionality
    const newItem = {
      id: Date.now(),
      name: product.name,
      price: product.price,
      originalPrice: product.originalPrice,
      quantity: 1,
      image: product.image,
      inStock: true,
    };

    setCartItems((prevItems) => [...prevItems, newItem]);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <CheckoutProgress currentStep={1} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">{t("loadingCart")}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        {/*  <CheckoutProgress currentStep={1} />*/}
        <EmptyCart />
      </div>
    );
  }

  return (
    <>
        <div className="min-h-screen bg-background">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6  lg:px-8 lg:py-24 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {t("shoppingCart")}
            </h1>
            <p className="text-muted-foreground mt-1">
              {cartItems.length}{" "}
              {cartItems.length === 1 ? t("item") : t("items")}{" "}
              {t("inYourCart")}
            </p>
          </div>

          {/* Clear Cart Button - Desktop 
          <div className="hidden sm:block">
            <Button
              variant="ghost"
              size="sm"
              iconName="Trash2"
              iconPosition="left"
              onClick={() => {
                setIsLoading(true);
                dispatch(
                  deleteCartService({ cart_id: cartItems[0]?.cart_id })
                ).then((response) => {
                  if (
                    response?.payload?.status == 200 ||
                    response?.payload?.status == 204
                  ) {
                    toast("deleted Succ");
                    dispatch(getMyCartService());
                    setCartItems([]);
                    setIsLoading(false);
                  }
                });
              }}
              className="text-error hover:text-error hover:bg-error/10"
            >
              {t("clearCart")}
            </Button>
          </div>*/}
        </div>

        {/* Main Content */}
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          {/* Cart Items - Left Column */}
          <div className="lg:col-span-8">
            {/* Mobile Clear Cart Button 
            <div className="sm:hidden mb-4">
              <Button
                variant="ghost"
                size="sm"
                iconName="Trash2"
                iconPosition="left"
                onClick={() => setCartItems([])}
                className="text-error hover:text-error hover:bg-error/10"
              >
                {t("clearCart")}
              </Button>
            </div>*/}

            {/* Cart Items List */}
            <div className="space-y-4 mb-8">
              {cartItems?.map((item) => (
                <CartItem
                  key={item.id}
                  item={item}
                  onUpdateQuantity={handleUpdateQuantity}
                  onRemoveItem={handleRemoveItem}
                  onMoveToWishlist={handleMoveToWishlist}
                  loadCartData={loadCartData}
                />
              ))}
            </div>

            {/* Recommended Products - Desktop 
            <div className="hidden lg:block">
              <RecommendedProducts onAddToCart={handleAddToCart} />
            </div>*/}
          </div>

          {/* Order Summary - Right Column */}

          <div className="lg:col-span-4 mt-8 lg:mt-0">
            <OrderSummary
              cartItems={cartItems}
              onApplyPromoCode={handleApplyPromoCode}
              onProceedToCheckout={handleProceedToCheckout}
              isLoading={isLoadingCart}
            />
          </div>
        </div>

        {/* Trust Signals
        <div className="mt-12 pt-8 border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-3">
                <Icon name="Truck" size={24} className="text-primary" />
              </div>
              <h3 className="font-semibold text-foreground mb-1">
                {t("freeShipping")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t("onOrdersOver50")}
              </p>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mb-3">
                <Icon name="RotateCcw" size={24} className="text-success" />
              </div>
              <h3 className="font-semibold text-foreground mb-1">
                {t("easyReturns")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t("thirtyDayReturnPolicy")}
              </p>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mb-3">
                <Icon name="Shield" size={24} className="text-accent" />
              </div>
              <h3 className="font-semibold text-foreground mb-1">
                {t("securePayment")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t("sslEncryptedCheckout")}
              </p>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center mb-3">
                <Icon name="Headphones" size={24} className="text-warning" />
              </div>
              <h3 className="font-semibold text-foreground mb-1">
                {t("support")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t("customerService")}
              </p>
            </div>
          </div>
        </div>*/}
      </div>
    </div>
    <Footer />
    </>

  );
};

export default ShoppingCart;
